import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import TabsNavigation from '#/components/basic/Button/TabsNavigation';
import ChartComponent from '#/components/chart/ChartComponent';
import MarketDepthChartWrap from '#/components/chart/depth/MarketDepthChartWrap';
import MarketDetailsWidget from '#/components/markets/detail/MarketDetailsWidget';
import OrderbookSection from '#/components/orders/orderBook/OrderbookSection';
import { useIsMobileBuySellRoute } from '#/components/trading/dashboardDetect';
import TradesTable from '#/components/trading/TradesTable';
import { selectChart } from '#/reduxState/exchange/selectedChartReducer';
import { staticDispatch, useSelector } from '#/reduxState/store';
import { scrollbarStyle } from '#/styleShared';

import { ChartsRouterSection } from './chartsRouterTypes';

const Wrap = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  background: var(--elevation-light);
  border-radius: 2px;
  /** Required for Chart iframe being rounded */
  overflow: hidden;
`;

const MarketsCenteredScrollable = styled.div`
  flex: 1;
  width: 100%;
  overflow-y: scroll;
  ${scrollbarStyle}

  display: flex;
  align-items: center;

  > div {
    max-width: 500px;
    margin: auto;
    padding: 16px;
  }
`;

const MobileFlexWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const ChartsRouter = ({
  section,
  onChange,
}: {
  section: ChartsRouterSection;
  onChange: (section: ChartsRouterSection) => void;
}) => {
  const isMobile = useSelector(state => state.screen.isMobile);
  const selectedChartType = useSelector(
    state => state.exchange.selectedChart.chartType
  );
  const isTradeRoute = useIsMobileBuySellRoute();

  const [tradingViewRendered, setTradingViewRendered] = useState(false);

  useEffect(() => {
    if (
      section === 'chart' &&
      selectedChartType === 'tradingView' &&
      !tradingViewRendered
    ) {
      // We want to keep TradingView in memory because of it's slow loading time
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTradingViewRendered(true);
    }
  }, [selectedChartType, section, tradingViewRendered]);

  const currentMarket = useSelector(state => state.markets.currentMarket);

  const { base, quote } = currentMarket;

  if (
    isTradeRoute &&
    !(
      section === 'chart' &&
      selectedChartType === 'tradingView' &&
      tradingViewRendered
    )
  ) {
    // Keep partial state (selected tab..) but release inner components until again needed
    return null;
  }

  return (
    <Wrap>
      <TabsNavigation
        buttons={[
          {
            title: 'Chart',
            onClick: () => {
              staticDispatch(selectChart('tradingView'));
              onChange('chart');
            },
            active: section === 'chart' && selectedChartType === 'tradingView',
            stretch: isMobile,
          },
          {
            title: 'Depth',
            onClick: () => {
              staticDispatch(selectChart('depth'));
              onChange('chart');
            },
            active: section === 'chart' && selectedChartType === 'depth',
            stretch: isMobile,
          },
          ...(isMobile
            ? [
                {
                  title: 'Order Book',
                  onClick: () => onChange('orderBook'),
                  active: section === 'orderBook',
                  stretch: true,
                },
                {
                  title: 'Trades',
                  onClick: () => onChange('trades'),
                  active: section === 'trades',
                  stretch: true,
                },
              ]
            : [
                {
                  title: 'Market Details',
                  onClick: () => onChange('marketDetails'),
                  active: section === 'marketDetails',
                },
              ]),
        ]}
      />
      {((section === 'chart' && selectedChartType === 'tradingView') ||
        tradingViewRendered) && (
        <div
          style={{
            // Keep in DOM hidden when already renderd to speed up tab transitions
            display:
              section === 'chart' && selectedChartType === 'tradingView'
                ? 'block'
                : 'none',
            height: '100%',
          }}
        >
          <ChartComponent />
        </div>
      )}

      {section === 'chart' && selectedChartType === 'depth' && (
        <MarketDepthChartWrap baseSymbol={base} quoteSymbol={quote} />
      )}
      {section === 'orderBook' && <OrderbookSection />}
      {section === 'trades' && (
        <MobileFlexWrap>
          <TradesTable />
        </MobileFlexWrap>
      )}
      {section === 'marketDetails' && base && quote && (
        <MarketsCenteredScrollable>
          <div style={{ width: 400 }}>
            <MarketDetailsWidget base={base} quote={quote} />
          </div>
        </MarketsCenteredScrollable>
      )}
    </Wrap>
  );
};

export default ChartsRouter;
